/**
 * HTML Mesh Helper for PlayCanvas — CSS3D Overlay Approach
 *
 * Renders real DOM elements (divs, iframes) in 3D space by positioning them
 * over the PlayCanvas canvas with CSS 3D transforms that match the camera's
 * perspective projection.  This is the same technique used by BabylonJS
 * HtmlMeshRenderer and Three.js CSS3DRenderer.
 *
 * Why CSS3D instead of canvas textures:
 *  - Iframes cannot be captured to a canvas (security / cross-origin)
 *  - Rich HTML (forms, videos, styled content) renders natively in the browser
 *  - Interactive content (links, inputs) works out of the box
 */
import * as pc from 'playcanvas';
import { htmlMeshPixelSize } from './htmlMeshSize';

// ---------------------------------------------------------------------------
// Public interfaces (unchanged from the previous API)
// ---------------------------------------------------------------------------

export interface HtmlMeshConfig {
    id: string;
    html?: string;
    htmlContent?: string;
    css?: string;
    position: { x: number; y: number; z: number };
    rotation?: { x: number; y: number; z: number };
    scale?: { x: number; y: number; z: number };
    width?: number;
    height?: number;
    animated?: boolean;
    updateRate?: number;
    billboard?: boolean;
    billboardRange?: { type: 'percentage' | 'waypoint'; start: number; end: number };
    visibilityRange?: { type: 'percentage' | 'waypoint'; start: number; end: number };
    /** Editor eyeball toggle — hide the mesh without deleting it. Default visible. */
    visible?: boolean;
    receiveShadows?: boolean;
    castShadows?: boolean;
    opacity?: number;
    doubleSided?: boolean;
}

/** Fields of a live mesh HtmlMeshManager.updateMesh changes (see there). */
export type HtmlMeshUpdate = Partial<Omit<HtmlMeshConfig, 'id'>> & {
    contentType?: string;
    iframeUrl?: string;
    formConfig?: unknown;
};

/** Config fields that decide what the mesh's DOM element contains. */
const HTML_MESH_CONTENT_KEYS = ['contentType', 'html', 'htmlContent', 'css', 'iframeUrl'] as const;

export interface HtmlMeshInstance {
    entity: pc.Entity;
    /** The DOM element positioned in CSS3D space */
    htmlElement: HTMLElement;
    config: HtmlMeshConfig;
    destroy: () => void;
    update: () => void;
    /** Pixel dimensions of the DOM element */
    pixelWidth: number;
    pixelHeight: number;
    /** Last-written style strings — skip redundant per-frame style writes when unchanged. */
    _lastTransform?: string;
    _lastDisplay?: string;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface HtmlMeshEntity extends pc.Entity {
    _billboardActive?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read a vector that may be in { x,y,z } or BabylonJS serialised { _x,_y,_z } format */
function readVec(
    v: { x?: number; y?: number; z?: number; _x?: number; _y?: number; _z?: number } | undefined,
    defaults: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
    if (!v) return defaults;
    return {
        x: (v as { _x?: number })._x ?? v.x ?? defaults.x,
        y: (v as { _y?: number })._y ?? v.y ?? defaults.y,
        z: (v as { _z?: number })._z ?? v.z ?? defaults.z,
    };
}

/** Clamp very small values to zero to avoid CSS precision issues */
function eps(v: number): number {
    return Math.abs(v) < 1e-10 ? 0 : v;
}

/**
 * Convert a PlayCanvas **view matrix** (camera.viewMatrix) to a CSS matrix3d.
 * Negates the 2nd row (indices 1,5,9,13 in column-major) to flip Y-up to CSS Y-down.
 */
function viewMatrixToCSS(d: Float32Array): string {
    return `matrix3d(${
        eps(d[0])},${eps(-d[1])},${eps(d[2])},${eps(d[3])},${
        eps(d[4])},${eps(-d[5])},${eps(d[6])},${eps(d[7])},${
        eps(d[8])},${eps(-d[9])},${eps(d[10])},${eps(d[11])},${
        eps(d[12])},${eps(-d[13])},${eps(d[14])},${eps(d[15])})`;
}

/**
 * Convert a PlayCanvas **world matrix** (entity.getWorldTransform()) to CSS matrix3d.
 * Negates the 2nd column (indices 4-7 in column-major) to flip the local Y axis
 * so CSS top-to-bottom maps to 3D bottom-to-top.
 */
function worldMatrixToCSS(d: Float32Array): string {
    return `matrix3d(${
        eps(d[0])},${eps(d[1])},${eps(d[2])},${eps(d[3])},${
        eps(-d[4])},${eps(-d[5])},${eps(-d[6])},${eps(-d[7])},${
        eps(d[8])},${eps(d[9])},${eps(d[10])},${eps(d[11])},${
        eps(d[12])},${eps(d[13])},${eps(d[14])},${eps(d[15])})`;
}

/** Find the first camera entity in the scene */
function findCameraEntity(app: pc.Application): pc.Entity | null {
    const cam = app.root.findComponent('camera');
    return cam ? cam.entity : null;
}

// Module-scope scratch for the per-frame billboard math (single-threaded, non-reentrant).
const _hmScale = new pc.Vec3();
const _hmQuat = new pc.Quat();
const _hmFlip = new pc.Quat().setFromEulerAngles(0, 180, 0);

// ---------------------------------------------------------------------------
// HtmlMeshManager
// ---------------------------------------------------------------------------

export class HtmlMeshManager {
    private app: pc.Application;
    private canvas: HTMLCanvasElement;
    private overlayContainer: HTMLDivElement;
    private cameraEl: HTMLDivElement;
    private meshes: Map<string, HtmlMeshInstance> = new Map();
    // Lazily-cached camera entity (invalidated on destroy) + last-written camera style strings, so
    // the per-frame update skips the findComponent walk and redundant style writes when static.
    private cachedCameraEntity: pc.Entity | null = null;
    private lastPerspective = '';
    private lastCameraTransform = '';

    constructor(app: pc.Application) {
        this.app = app;
        this.canvas = app.graphicsDevice.canvas as HTMLCanvasElement;

        // ---- Build the CSS3D overlay DOM structure ----
        // Container: same size as the canvas, positioned on top
        this.overlayContainer = document.createElement('div');
        Object.assign(this.overlayContainer.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: '1',
        });

        // Camera element: receives the inverse-view transform
        this.cameraEl = document.createElement('div');
        Object.assign(this.cameraEl.style, {
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '0',
            height: '0',
            transformStyle: 'preserve-3d',
        });

        this.overlayContainer.appendChild(this.cameraEl);

        // Insert overlay as sibling of the canvas
        const parent = this.canvas.parentElement;
        if (parent) {
            if (getComputedStyle(parent).position === 'static') {
                parent.style.position = 'relative';
            }
            parent.appendChild(this.overlayContainer);
        }

        // Start the per-frame update
        app.on('update', this.onUpdate, this);

        console.log('[HtmlMeshManager] CSS3D overlay initialised');
    }

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    createMesh(config: HtmlMeshConfig): HtmlMeshInstance {
        // A mesh already registered under this id would otherwise be orphaned: its DOM element
        // left on screen and its entity in the scene, out of reach of destroyMesh.
        if (this.meshes.has(config.id)) this.destroyMesh(config.id);

        // Sanitise pixel dimensions
        const { width: pw, height: ph } = htmlMeshPixelSize(config.width, config.height);
        const scaleVec = readVec(config.scale, { x: 1, y: 1, z: 1 });
        const worldWidth = scaleVec.x * (pw / ph);
        const worldHeight = scaleVec.y;

        // 3D entity (invisible — used only for transform tracking)
        const entity = this.createEntity(config, pw, ph);
        // Honor the editor eyeball toggle. onUpdate hides the DOM when disabled.
        entity.enabled = config.visible !== false;

        // DOM element in CSS3D space
        const domEl = this.createDomElement(config, pw, ph);
        this.cameraEl.appendChild(domEl);

        const instance: HtmlMeshInstance = {
            entity,
            htmlElement: domEl,
            config,
            pixelWidth: pw,
            pixelHeight: ph,
            destroy: () => this.destroyMesh(config.id),
            update: () => { /* no-op for CSS3D — content is live DOM */ },
        };

        this.meshes.set(config.id, instance);

        console.log(
            `[HtmlMeshManager] Created CSS3D mesh: ${config.id}, ` +
            `pixels=${pw}x${ph}, world=${worldWidth.toFixed(2)}x${worldHeight.toFixed(2)}`,
        );

        return instance;
    }

    destroyMesh(id: string): void {
        const inst = this.meshes.get(id);
        if (!inst) return;
        inst.entity.destroy();
        inst.htmlElement.remove();
        this.meshes.delete(id);
    }

    /**
     * Apply changed fields to a live mesh exactly as re-creating it from the merged config
     * would, without touching what did not change: the transform parts given, the pixel size
     * (DOM element size and the aspect its world width is scaled by), the content (a content
     * field replaces only the DOM element, so other meshes' iframes do not reload and the
     * entity keeps its identity), and the editor visibility toggle (the caller re-runs
     * updateVisibility for the visibility range). Opacity and formConfig are only stored: the
     * element always renders at full opacity and forms are not rendered here. Billboarding is
     * not applied in place (turning it off has to restore the authored rotation); re-create
     * the mesh for that. Returns false when no mesh has this id.
     */
    updateMesh(id: string, data: HtmlMeshUpdate): boolean {
        const inst = this.meshes.get(id);
        if (!inst) return false;
        const has = (key: string) => Object.prototype.hasOwnProperty.call(data, key);
        const config: HtmlMeshConfig = { ...inst.config, ...data, id: inst.config.id };
        inst.config = config;

        const resized = has('width') || has('height');
        if (resized) {
            const { width, height } = htmlMeshPixelSize(config.width, config.height);
            inst.pixelWidth = width;
            inst.pixelHeight = height;
            inst.htmlElement.style.width = `${width}px`;
            inst.htmlElement.style.height = `${height}px`;
        }
        if (HTML_MESH_CONTENT_KEYS.some(has)) {
            const replacement = this.createDomElement(config, inst.pixelWidth, inst.pixelHeight);
            inst.htmlElement.replaceWith(replacement);
            inst.htmlElement = replacement;
            inst._lastDisplay = undefined;
        }
        // Only the transform parts given: a gizmo rotation is applied to the entity directly and
        // never reaches this config, so re-applying the stored rotation would undo it.
        if (has('position')) this.applyPosition(inst.entity, config);
        if (has('rotation')) this.applyRotation(inst.entity, config);
        if (has('scale') || resized) this.applyScale(inst.entity, config, inst.pixelWidth, inst.pixelHeight);
        if (has('visible') || has('visibilityRange')) inst.entity.enabled = config.visible !== false;
        // Make the next frame write the element's transform and display.
        inst._lastTransform = undefined;
        return true;
    }

    getMesh(id: string): HtmlMeshInstance | undefined {
        return this.meshes.get(id);
    }

    getAllMeshes(): Map<string, HtmlMeshInstance> {
        return this.meshes;
    }

    updateVisibility(scrollPercent: number, waypointIndex: number, isLoopMode?: boolean): void {
        const inRange = (value: number, start: number, end: number): boolean => {
            if (start <= end) return value >= start && value <= end;
            // Wrap-around: start > end only meaningful in loop mode (e.g., 80→100→0→20)
            return isLoopMode ? (value >= start || value <= end) : false;
        };
        this.meshes.forEach((inst) => {
            const cfg = inst.config;

            if (cfg.visibilityRange) {
                const r = cfg.visibilityRange;
                let inWindow = true;
                if (r.type === 'percentage') inWindow = inRange(scrollPercent, r.start, r.end);
                else if (r.type === 'waypoint') inWindow = inRange(waypointIndex, r.start, r.end);
                // Respect the editor eyeball toggle too — a hidden mesh stays hidden.
                inst.entity.enabled = (cfg.visible !== false) && inWindow;
            }

            if (cfg.billboard && cfg.billboardRange) {
                const r = cfg.billboardRange;
                let active = false;
                if (r.type === 'percentage') active = inRange(scrollPercent, r.start, r.end);
                else if (r.type === 'waypoint') active = inRange(waypointIndex, r.start, r.end);
                (inst.entity as HtmlMeshEntity)._billboardActive = active;
            } else if (cfg.billboard) {
                (inst.entity as HtmlMeshEntity)._billboardActive = true;
            }
        });
    }

    updateMeshTexture(_id: string): void {
        // No-op — CSS3D content is live DOM, updates automatically
    }

    destroy(): void {
        this.meshes.forEach((_, id) => this.destroyMesh(id));
        this.app.off('update', this.onUpdate, this);
        this.overlayContainer.remove();
    }

    // -----------------------------------------------------------------------
    // Internals
    // -----------------------------------------------------------------------

    /**
     * Create a renderless entity to track the 3D transform.
     * No render component — purely a transform node. The visible content is a CSS3D
     * DOM element composited OVER the WebGL canvas, which is why HTML meshes always
     * render in front of the splats (they're not WebGL geometry, so they can't join
     * the depth pipeline the way hotspot/mirror/custom-mesh planes do via depthWrite).
     *
     * TODO(html-in-canvas): once the HTML-in-canvas / drawElement() API ships
     * cross-browser (https://html-in-canvas.dev), add a HYBRID mode — render
     * SAME-ORIGIN / static HTML meshes as a WebGL textured plane (drawElement -> texture
     * on a plane in the hotspotMeshLayer with BLEND_PREMULTIPLIED + depthWrite, mirroring
     * applyOpacityToCustomMesh in createViewer.ts) so they DEPTH-MIX with the splats (and
     * opacity can return), while keeping CSS3D-on-top for live CROSS-ORIGIN iframes/forms
     * (which drawElement can't capture and which need real pointer interactivity).
     */
    private createEntity(config: HtmlMeshConfig, pixelWidth: number, pixelHeight: number): pc.Entity {
        const entity = new pc.Entity(`htmlMesh-${config.id}`);
        this.applyPosition(entity, config);
        this.applyRotation(entity, config);
        this.applyScale(entity, config, pixelWidth, pixelHeight);
        this.app.root.addChild(entity);
        return entity;
    }

    /** Position — negate Z for BabylonJS LH -> PlayCanvas RH */
    private applyPosition(entity: pc.Entity, config: HtmlMeshConfig): void {
        const pos = readVec(config.position, { x: 0, y: 0, z: 0 });
        entity.setPosition(pos.x, pos.y, -pos.z);
    }

    /** Rotation — BabylonJS YXZ Euler (radians, LH) -> PlayCanvas quaternion (RH) */
    private applyRotation(entity: pc.Entity, config: HtmlMeshConfig): void {
        const rot = readVec(config.rotation, { x: 0, y: 0, z: 0 });
        const hy = rot.y / 2, hx = rot.x / 2, hz = rot.z / 2;
        const cy = Math.cos(hy), sy = Math.sin(hy);
        const cx = Math.cos(hx), sx = Math.sin(hx);
        const cz = Math.cos(hz), sz = Math.sin(hz);
        const qw = cy * cx * cz + sy * sx * sz;
        const qx = cy * sx * cz + sy * cx * sz;
        const qy = sy * cx * cz - cy * sx * sz;
        const qz = cy * cx * sz - sy * sx * cz;
        // LH -> RH: negate x and y components
        entity.setRotation(-qx, -qy, qz, qw);
    }

    /** Scale encodes the world-space dimensions of the mesh: scale.x times the pixel aspect wide, scale.y high */
    private applyScale(entity: pc.Entity, config: HtmlMeshConfig, pixelWidth: number, pixelHeight: number): void {
        const scaleVec = readVec(config.scale, { x: 1, y: 1, z: 1 });
        entity.setLocalScale(scaleVec.x * (pixelWidth / pixelHeight), scaleVec.y, 1);
    }

    /** Build the DOM element (iframe or div) for CSS3D rendering */
    private createDomElement(config: HtmlMeshConfig, pw: number, ph: number): HTMLDivElement {
        const wrapper = document.createElement('div');
        wrapper.id = `html-mesh-${config.id}`;
        Object.assign(wrapper.style, {
            position: 'absolute',
            width: `${pw}px`,
            height: `${ph}px`,
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden',
            pointerEvents: 'auto',
            overflow: 'hidden',
            // Opacity locked to 100%. Unlike the WebGL meshes (hotspots/portals/custom
            // meshes), HTML meshes are CSS3D DOM composited OVER the canvas — they're
            // outside the splat depth pipeline and always render in front of the scene, by
            // design (live, interactive iframes/forms). Hard-coding '1' keeps them reading
            // as solid panels and neutralizes any opacity<1 in already-saved scene JSON.
            opacity: '1',
        });

        const contentType = (config as HtmlMeshConfig & { contentType?: string }).contentType || 'html';
        const iframeUrl = (config as HtmlMeshConfig & { iframeUrl?: string }).iframeUrl;
        const htmlContent = config.html || config.htmlContent || '';

        if (contentType === 'iframe' && iframeUrl) {
            // Real iframe — renders the actual website
            const iframe = document.createElement('iframe');
            iframe.src = iframeUrl;
            Object.assign(iframe.style, {
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
                background: '#fff',
            });
            iframe.setAttribute('allow', 'fullscreen; autoplay; encrypted-media');
            iframe.setAttribute(
                'sandbox',
                'allow-scripts allow-same-origin allow-forms allow-popups allow-presentation',
            );
            wrapper.appendChild(iframe);
        } else if (htmlContent) {
            if (config.css) {
                const style = document.createElement('style');
                style.textContent = config.css;
                wrapper.appendChild(style);
            }
            const content = document.createElement('div');
            content.style.width = '100%';
            content.style.height = '100%';
            // Content comes from scene author's own scene data
            content.innerHTML = htmlContent; // eslint-disable-line no-unsanitized/property
            wrapper.appendChild(content);
        } else {
            // Empty placeholder
            Object.assign(wrapper.style, {
                background: 'rgba(26, 26, 46, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontFamily: 'sans-serif',
            });
            const placeholder = document.createElement('div');
            placeholder.style.textAlign = 'center';
            const icon = document.createElement('div');
            icon.style.fontSize = '24px';
            icon.style.marginBottom = '8px';
            icon.textContent = '</>';
            const label = document.createElement('div');
            label.textContent = 'HTML Content';
            placeholder.appendChild(icon);
            placeholder.appendChild(label);
            wrapper.appendChild(placeholder);
        }

        return wrapper;
    }

    /**
     * Per-frame update: recompute CSS perspective + transforms so the DOM
     * elements track the PlayCanvas camera exactly.
     */
    private onUpdate = (): void => {
        if (this.meshes.size === 0) return;

        // Camera lookup cached lazily on first find (constructor-time caching is fragile with the
        // editor's lazy manager path); invalidated when the entity is destroyed.
        let cameraEntity = this.cachedCameraEntity;
        if (!cameraEntity?.camera) {
            cameraEntity = findCameraEntity(this.app);
            if (cameraEntity) {
                this.cachedCameraEntity = cameraEntity;
                cameraEntity.once('destroy', () => { this.cachedCameraEntity = null; });
            }
        }
        if (!cameraEntity?.camera) return;

        const camera = cameraEntity.camera;
        const canvasH = this.canvas.clientHeight;

        // CSS perspective = projectionMatrix[5] * halfHeight
        // projectionMatrix[5] = 1 / tan(fov/2)  (vertical FOV)
        const projData = camera.projectionMatrix.data as Float32Array;
        const fov = projData[5] * (canvasH / 2);

        // Style writes only when the strings actually changed (camera static ⇒ zero DOM writes).
        const perspective = `${fov}px`;
        if (perspective !== this.lastPerspective) {
            this.lastPerspective = perspective;
            this.overlayContainer.style.perspective = perspective;
        }

        // Camera element: translateZ(fov) then the CSS-ified view matrix
        const viewData = camera.viewMatrix.data as Float32Array;
        const cameraTransform = `translateZ(${fov}px) ${viewMatrixToCSS(viewData)}`;
        if (cameraTransform !== this.lastCameraTransform) {
            this.lastCameraTransform = cameraTransform;
            this.cameraEl.style.transform = cameraTransform;
        }

        // Billboard handling — face the camera before we read the world matrix
        const camPos = cameraEntity.getPosition();

        this.meshes.forEach((inst) => {
            // Billboard
            if (inst.config.billboard) {
                const active = (inst.entity as HtmlMeshEntity)._billboardActive ?? true;
                if (active) {
                    _hmScale.copy(inst.entity.getLocalScale());
                    inst.entity.lookAt(camPos);
                    // lookAt faces the entity TOWARD the target; add 180 Y so the
                    // DOM front (+Z in CSS) points at the camera
                    _hmQuat.copy(inst.entity.getRotation());
                    inst.entity.setRotation(_hmQuat.mul(_hmFlip));
                    inst.entity.setLocalScale(_hmScale);
                }
            }

            // Visibility (write only on transitions)
            const display = inst.entity.enabled ? '' : 'none';
            if (display !== inst._lastDisplay) {
                inst._lastDisplay = display;
                inst.htmlElement.style.display = display;
            }
            if (display === 'none') return;

            // World matrix -> CSS transform
            // The world matrix includes position, rotation, scale(worldW, worldH, 1).
            // The DOM element is pw x ph CSS pixels. We shrink it to 1x1 first with
            // scale(1/pw, 1/ph), then the world matrix stretches it to the correct
            // world size. translate(-50%, -50%) centres the element on its origin.
            const wd = inst.entity.getWorldTransform().data as Float32Array;
            const transform =
                `translate(-50%, -50%) ${worldMatrixToCSS(wd)} scale(${
                    eps(1 / inst.pixelWidth)}, ${eps(1 / inst.pixelHeight)})`;
            if (transform !== inst._lastTransform) {
                inst._lastTransform = transform;
                inst.htmlElement.style.transform = transform;
            }
        });
    };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function setupHtmlMeshes(app: pc.Application, htmlMeshes: HtmlMeshConfig[]): HtmlMeshManager {
    const manager = new HtmlMeshManager(app);
    console.log(`[HtmlMeshManager] Setting up ${htmlMeshes.length} HTML meshes (CSS3D)`);
    for (const sceneConfig of htmlMeshes) {
        if (!sceneConfig || typeof sceneConfig !== 'object') continue;
        // createMesh replaces a mesh already registered under the same id (the editor re-adds
        // meshes by id). A scene's own list is different: two meshes that share an id, or have
        // none (an old or hand-edited scene), are two meshes and both show, as they always did.
        // The later one is registered under a unique key instead.
        let config = sceneConfig;
        if (manager.getMesh(config.id)) {
            let n = 2;
            while (manager.getMesh(`${config.id}~${n}`)) n++;
            config = { ...config, id: `${config.id}~${n}` };
        }
        console.log(`[HtmlMeshManager] Creating mesh: ${config.id}`, {
            html: (config.html || config.htmlContent || '').substring(0, 80),
            contentType: (config as HtmlMeshConfig & { contentType?: string }).contentType,
            iframeUrl: (config as HtmlMeshConfig & { iframeUrl?: string }).iframeUrl,
            position: config.position,
            scale: config.scale,
            width: config.width,
            height: config.height,
        });
        manager.createMesh(config);
    }
    return manager;
}
